import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './index.css';
import App from './App';
import { queryClient } from './lib/react-query';

// Suppress benign ResizeObserver loop warnings before the dev overlay catches them.
// Browser layout libraries can trigger these without a functional app error.
const resizeObserverLoopMessages = new Set([
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]);

const isResizeObserverLoopMessage = (message: unknown): boolean =>
  typeof message === 'string' && resizeObserverLoopMessages.has(message);

window.addEventListener(
  'error',
  (event) => {
    if (isResizeObserverLoopMessage(event.message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: unknown } | string | undefined;
  const message = typeof reason === 'string' ? reason : reason?.message;
  if (isResizeObserverLoopMessage(message)) {
    event.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const showReactQueryDevtools =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_QUERY_DEVTOOLS === 'true';

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {showReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);
